// The ruler's arithmetic, with no DOM in it.
//
// The bottom-edge ruler is twelve month cells of three ten-day sections each.
// The sections were briefly drawn at a height proportional to the rating there —
// the handoff's idea, so the timeline would answer "when is this place good"
// unasked — and it was dropped after use: a row of 36 bars at 36 different
// heights reads as a chart, and a reader trying to CLICK one has to work out
// which stripe is a target. They are uniform now, and the question they were
// answering is the briefing calendar's, which has the room to answer it
// properly.
//
// What is left is pure position, and it is pure, so it lives here beside the
// other environment-neutral libraries rather than inside the widget.
//
// Note the ruler is generic in `n` (the number of slots) exactly as the squares
// it replaces were. n is 36 in practice — 12 months × 3 dekads, see
// sections.ts — but nothing here assumes it.

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

// Where the playback marker sits, as a percentage of the ruler's width.
//
// Centred on its slot rather than at the slot's left edge: a dekad is a ten-day
// span, and the marker claims the middle of it. That is also what keeps the
// marker inside the ruler at both ends — at slot 0 of 36 it sits at 1.4%, not 0.
export function markerLeftPct(idx: number, n: number): number {
  if (n <= 0) return 0;
  return ((clamp(idx, 0, n - 1) + 0.5) / n) * 100;
}

// Where the marker sits PART WAY through a dekad, as a percentage.
//
// The data steps — one frame per ten days, and the field crossfades between
// them — but time does not, and the marker is a clock rather than a readout.
// A quartz watch ticks once a second; a good mechanical one sweeps, because the
// second hand is showing you a continuous quantity. Same here: over the seconds
// a frame is shown, the marker crosses that frame's ten days and arrives at the
// next dekad's centre exactly as the frame flips, so there is no jump at all.
//
// `progress` is 0–1 through the current step. At 0 this is markerLeftPct.
//
// The last dekad sweeps toward a thirty-seventh that does not exist, so the
// result is clamped to the ruler's right edge: the year ends there, and the
// marker waits at the edge for the wrap rather than running off it.
export function sweepLeftPct(idx: number, progress: number, n: number): number {
  if (n <= 0) return 0;
  const p = clamp(Number.isFinite(progress) ? progress : 0, 0, 1);
  return clamp(((clamp(idx, 0, n - 1) + 0.5 + p) / n) * 100, 0, 100);
}

// Which slot a horizontal position falls in, given as a 0–1 fraction of the
// ruler's width. The inverse of markerLeftPct, and the mapping behind both the
// hover preview and the drag scrub.
//
// f === 1 is the right edge, which belongs to the LAST slot, not to a
// thirty-seventh that does not exist — hence the clamp on the way out rather
// than a bare floor.
export function idxFromFraction(f: number, n: number): number {
  if (n <= 0) return 0;
  return clamp(Math.floor(clamp(f, 0, 1) * n), 0, n - 1);
}
